import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import Header from "@/components/Header";

const LAST_UPDATED = "January 2026";

export default function TermsPage() {
  return (
    <div className="min-h-screen w-full flex flex-col text-zinc-900">
      <Header />
      <section className="max-w-3xl mx-auto w-full px-6 md:px-12 pt-12 pb-24">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 font-mono-plex text-[10px] font-semibold text-zinc-700 hover:text-zinc-950 mb-6"
        >
          <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
          BACK TO TRUESTREAM
        </Link>

        <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-3">
          LEGAL · LAST UPDATED {LAST_UPDATED.toUpperCase()}
        </div>
        <h1 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight text-zinc-950 mb-8">
          Terms of Service
        </h1>

        <div className="prose prose-zinc max-w-none space-y-6 text-[15px] leading-relaxed text-zinc-800">
          <p>
            Welcome to TrueStream. By using our web app, Android app, or shared verdict links, you agree to
            these Terms of Service. If you don&apos;t agree, please don&apos;t use the service.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">What TrueStream is</h2>
          <p>
            TrueStream is a fact-checking assistant. Our five-agent pipeline extracts factual claims from
            public social-media videos and compares them against authoritative sources (fact-checkers via
            Google Fact Check Tools, Wikipedia, and an LLM research analyst). It labels each claim TRUE,
            FALSE, PARTIALLY_TRUE, OUTDATED, or UNVERIFIABLE.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Not a substitute for journalism</h2>
          <p>
            Verdicts are best-effort and generated by automated agents. They may be wrong. Always read the
            underlying sources we cite and use your own judgement before sharing or acting on a claim.
            TrueStream does not warrant that verdicts are complete, correct, or current, and is not liable
            for decisions you make based on them.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Acceptable use</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Only submit URLs to publicly-viewable videos you have the right to view.</li>
            <li>Don&apos;t use TrueStream to harass individuals or coordinate defamation campaigns.</li>
            <li>Don&apos;t scrape, wrap, or resell our API beyond the rate limit (30 requests / minute per IP).</li>
            <li>Don&apos;t attempt to bypass the anonymous free-check quota by cycling browsers or scripts.</li>
            <li>Don&apos;t upload copyrighted material — we only accept public URLs.</li>
          </ul>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Your account</h2>
          <p>
            Sign-in is optional and goes through Google. You&apos;re responsible for keeping your Google
            account secure. If we detect abuse tied to an account we may suspend or delete it without
            notice.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Content ownership</h2>
          <p>
            The claims we detect are quoted from the videos you submit — the underlying content belongs to
            its original creators. Our verdict text, source rankings, and pipeline outputs are provided
            under a permissive licence for personal, journalistic, and educational reference. You may
            share verdict pages (the <code>/check/</code> deep links) freely.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Availability and changes</h2>
          <p>
            TrueStream is a beta service. We may add features, remove features, change rate limits, or
            take the service down for maintenance without prior notice. During beta the service is free —
            we&apos;ll give notice before any paid tier launches.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Termination</h2>
          <p>
            You can stop using TrueStream at any time. We may suspend your access if you violate these
            terms. On termination, cached data expires per the TTL rules in our{" "}
            <Link className="underline" to="/privacy">Privacy Policy</Link>.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Governing law</h2>
          <p>
            These terms are governed by the laws applicable to the entity operating TrueStream. Disputes
            will first be attempted to be resolved informally by writing to us; unresolved disputes go to
            the competent courts.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Contact</h2>
          <p>
            Questions or concerns: <a className="underline" href="mailto:hello@truestream.app">hello@truestream.app</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
